import React, { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { logUIError } from "../../../../../services/api-misc";

const COMMON_SYSTEM_OPTIONS = [
  "4D EMR",
  "Epic",
  "Cerner",
  "athenahealth",
  "eClinicalWorks",
  "NextGen",
  "Allscripts",
  "DrChrono",
  "Nextech",
  "AdvancedMD",
  "Practice Fusion",
  "Kareo",
  "Tebra",
  "OpenDental",
  "Dentrix",
  "Pipedrive",
  "GoHighLevel V1",
  "HubSpot",
  "Salesforce",
  "Zoho CRM",
  "GoHighLevel",
  "Other",
];

const EMRIntegrationRequestPrompt = ({ session }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [preferredContactEmail, setPreferredContactEmail] = useState("");
  const [integrationSystem, setIntegrationSystem] = useState("");
  const [showSystemSuggestions, setShowSystemSuggestions] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestError, setRequestError] = useState("");

  const integrationSuggestions = useMemo(() => {
    const trimmed = integrationSystem.trim().toLowerCase();
    if (!trimmed) return COMMON_SYSTEM_OPTIONS;

    return COMMON_SYSTEM_OPTIONS.filter((option) =>
      option.toLowerCase().includes(trimmed),
    );
  }, [integrationSystem]);

  const openModal = useCallback(async () => {
    const initialEmail = session?.userEmail || "";
    setPreferredContactEmail(initialEmail);
    setIntegrationSystem("");
    setShowSystemSuggestions(true);
    setRequestError("");
    setIsModalVisible(true);

    try {
      await logUIError({
        message: "Consults empty-state EMR integration request opened",
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || undefined,
        context: {
          component: "EMRIntegrationRequestPrompt",
          action: "request_emr_integration_opened",
          preferredContactEmail: initialEmail || null,
        },
      });
    } catch (error) {
      console.warn("Failed to log integration request open event:", error);
    }
  }, [session]);

  const handleSubmit = useCallback(async () => {
    const trimmedEmail = preferredContactEmail.trim();
    const trimmedSystem = integrationSystem.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);

    if (!trimmedEmail || !isValidEmail) {
      setRequestError("Enter a valid preferred contact email to continue.");
      return;
    }

    if (!trimmedSystem) {
      setRequestError("Enter or select your EMR or CRM.");
      return;
    }

    setRequestError("");
    setIsSubmittingRequest(true);

    try {
      await logUIError({
        message: "Consults EMR integration requested",
        page_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        user_email: session?.userEmail || trimmedEmail || undefined,
        clinic_name: session?.clinicName || undefined,
        clinic_id: session?.clinicId || undefined,
        context: {
          component: "EMRIntegrationRequestPrompt",
          action: "request_emr_integration_submitted",
          requesterEmail: session?.userEmail || null,
          requesterUserId: session?.userId || null,
          preferredContactEmail: trimmedEmail,
          requestedSystem: trimmedSystem,
        },
      });

      setIsModalVisible(false);
    } catch (error) {
      setRequestError(
        "Could not submit your request right now. Please try again.",
      );
      console.warn("Failed to log integration request:", error);
    } finally {
      setIsSubmittingRequest(false);
    }
  }, [integrationSystem, preferredContactEmail, session]);

  return (
    <View style={styles.container}>
      <Text style={styles.text}>
        Speak to our team to sync the appointments from your EMR or CRM
      </Text>
      <TouchableOpacity style={styles.button} onPress={openModal}>
        <Text style={styles.buttonText}>Request Free Integration</Text>
      </TouchableOpacity>

      <Modal
        visible={isModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Request EMR integration</Text>

            <Text style={styles.modalLabel}>Preferred contact email</Text>
            <TextInput
              style={styles.modalInput}
              value={preferredContactEmail}
              onChangeText={setPreferredContactEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              placeholder="name@clinic.com"
              onFocus={() => setShowSystemSuggestions(false)}
            />

            <Text style={styles.modalLabel}>EMR or CRM</Text>
            <TextInput
              style={styles.modalInput}
              value={integrationSystem}
              onChangeText={(value) => {
                setIntegrationSystem(value);
                setShowSystemSuggestions(true);
              }}
              placeholder="Select or type your EMR/CRM"
              onFocus={() => setShowSystemSuggestions(true)}
            />
            {showSystemSuggestions && integrationSuggestions.length > 0 && (
              <ScrollView
                style={styles.suggestionsList}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                keyboardShouldPersistTaps="handled"
              >
                {integrationSuggestions.map((option) => (
                  <TouchableOpacity
                    key={option}
                    style={styles.suggestionItem}
                    onPress={() => {
                      setIntegrationSystem(option);
                      setShowSystemSuggestions(false);
                    }}
                  >
                    <Text style={styles.suggestionText}>{option}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            )}

            {requestError ? (
              <Text style={styles.errorText}>{requestError}</Text>
            ) : null}

            <View style={styles.actions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={() => setIsModalVisible(false)}
                disabled={isSubmittingRequest}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.submitButton,
                  isSubmittingRequest ? styles.submitButtonDisabled : null,
                ]}
                onPress={handleSubmit}
                disabled={isSubmittingRequest}
              >
                {isSubmittingRequest ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>Submit request</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 16,
  },
  text: {
    fontSize: 15,
    color: "#333333",
    textAlign: "center",
    lineHeight: 22,
    marginBottom: 12,
  },
  button: {
    backgroundColor: "#0085FF",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    width: "100%",
    alignItems: "center",
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "500",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.5)",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modalCard: {
    width: "100%",
    maxWidth: 520,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#333333",
    marginBottom: 16,
  },
  modalLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333333",
    marginBottom: 8,
  },
  modalInput: {
    height: 44,
    borderWidth: 1,
    borderColor: "#C4C6CD",
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    color: "#1A1A1A",
    backgroundColor: "#FFFFFF",
    marginBottom: 12,
  },
  suggestionsList: {
    borderWidth: 1,
    borderColor: "#E0E0E0",
    borderRadius: 8,
    marginTop: -4,
    marginBottom: 12,
    maxHeight: 180,
    overflow: "hidden",
  },
  suggestionItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  suggestionText: {
    fontSize: 15,
    color: "#333333",
  },
  errorText: {
    fontSize: 14,
    color: "#D32F2F",
    marginBottom: 12,
  },
  actions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    marginTop: 4,
  },
  cancelButton: {
    borderWidth: 1,
    borderColor: "#C4C6CD",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minHeight: 44,
    justifyContent: "center",
  },
  cancelButtonText: {
    color: "#333333",
    fontSize: 15,
    fontWeight: "500",
  },
  submitButton: {
    backgroundColor: "#0085FF",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginLeft: 10,
    minHeight: 44,
    minWidth: 130,
    justifyContent: "center",
    alignItems: "center",
  },
  submitButtonDisabled: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
  },
});

export default EMRIntegrationRequestPrompt;
